//@ts-ignore
import * as api from '@yandex-int/k-common/client/api';
import { EXTERNAL_LMS_SLUGS, ExternalLmsServerDates, SYNC_STATUS } from '@yandex-int/k-common/typings';
import { showIntlMessageNotification } from 'platform-components';
import { NOTIFICATION_THEMES } from 'platform-components/constants';
import { all, put, select, takeLatest } from 'redux-saga/effects';

import { LESSON_DATES } from 'common.components/constants';
import * as lessonViewActions from 'common.components/lesson-view.store/actions';
import * as lessonSelectors from 'common.components/lesson/selectors';
import { syncTeamRequest } from 'common.components/user/actions';
import * as userSelectors from 'common.components/user/selectors';
import { getTeamIdFromPath } from 'utils/path';

import * as actions from './actions';
import * as constants from './constants';

export function* assignmentSagasWatcher() {
    yield all([
        takeLatest(constants.LOAD_EXTERNAL_LMS_AVAILABLE_DATES_REQUEST, getExternalLmsAvailableDatesSaga),
        takeLatest(constants.HANDLE_EXTERNAL_LMS_CHECKBOX_TOGGLE, handleExternalLmsCheckboxToggleSaga),
        takeLatest(constants.FORCE_SHOULD_ASSIGN_IN_EXTERNAL_LMS, forceShouldAssignInExternalLmsSaga),
    ]);
}

// eslint-disable-next-line complexity
function* getExternalLmsAvailableDatesSaga(action: ReturnType<typeof actions.loadExternalLmsAvailableDatesRequest>) {
    try {
        const { teamId, intl } = action;
        const lms: string | null = yield select(lessonSelectors.getLessonCourseExternalLmsSlug, { classId: teamId });
        const externalSubjectId: string | null = yield select(lessonSelectors.getCourseExternalLmsSubjectId, {
            classId: teamId,
        });
        const dates: ExternalLmsServerDates = yield api.getExternalLmsAvailableDates({
            teamId: teamId!,
            externalSubjectId: externalSubjectId!,
            lms: lms as EXTERNAL_LMS_SLUGS,
        });

        yield put(actions.loadExternalLmsAvailableDatesSuccess(dates));
        const { lesson_start_datetimes: lessonStartDates, lesson_finish_datetimes: lessonFinishDates } = dates;
        let assignDateErrorMessage, deadlineDateErrorMessage;

        if (lessonStartDates && lessonStartDates.length > 0) {
            yield put(
                actions.setAssignDate({
                    type: LESSON_DATES.CUSTOM,
                    customDate: new Date(dates.lesson_start_datetimes[0]),
                })
            );
        } else {
            assignDateErrorMessage = intl.formatMessage({ id: 'assignLessonModal.errors.lms.noAvailableStartDates' });
        }

        if (lessonFinishDates && lessonFinishDates.length > 0) {
            yield put(
                actions.setDeadlineDate({
                    type: LESSON_DATES.CUSTOM,
                    customDate: new Date(dates.lesson_finish_datetimes[0]),
                })
            );
        } else {
            deadlineDateErrorMessage = intl.formatMessage({
                id: 'assignLessonModal.errors.lms.noAvailableFinishDates',
            });
        }

        if (assignDateErrorMessage && deadlineDateErrorMessage) {
            const errorMessage = intl.formatMessage({ id: 'assignLessonModal.errors.lms.noAvailableDates' });

            yield put(actions.setExternalLmsDatesError(errorMessage));
        } else if (assignDateErrorMessage) {
            yield put(actions.setAssignDateError(assignDateErrorMessage));
        } else if (deadlineDateErrorMessage) {
            yield put(actions.setDeadlineDateError(deadlineDateErrorMessage));
        }
    } catch (e) {
        yield put(actions.loadExternalLmsAvailableDatesError());
        yield put(lessonViewActions.setShouldAssignInExternalLms(false));
        yield put(
            showIntlMessageNotification({
                headerId: 'toast.lmsDatesFail.header',
                messageId: 'toast.lmsDatesFail.description',
                theme: NOTIFICATION_THEMES.FAIL,
                mix: 'qa_lms-dates-error',
            })
        );
    }
}

function* syncTeamBeforeAssignInnExternalLms(teamId: number | null) {
    const syncStatus: SYNC_STATUS = yield select(userSelectors.getCurrentClassSyncStatus, { classId: teamId });
    if (syncStatus !== SYNC_STATUS.READY && teamId !== null) {
        yield put(syncTeamRequest(teamId));
    }
}

function* handleExternalLmsCheckboxToggleSaga(action: ReturnType<typeof actions.handleExternalLmsCheckboxToggle>) {
    const { checked, teamId, intl } = action;
    yield put(lessonViewActions.setShouldAssignInExternalLms(checked));

    if (checked) {
        yield put(actions.loadExternalLmsAvailableDatesRequest(teamId, intl));

        yield syncTeamBeforeAssignInnExternalLms(teamId);
    }
}

function* forceShouldAssignInExternalLmsSaga() {
    const teamId = getTeamIdFromPath();

    yield put(lessonViewActions.setShouldAssignInExternalLms(true));

    yield syncTeamBeforeAssignInnExternalLms(teamId);
}
